import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, Validators } from '@angular/forms';
import { LayoutService } from 'src/app/layout/service/app.layout.service';
import { LoginService } from './login.service';
import { StorageHelperService } from '../core/services/storage-helper.service';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { CountdownComponent, CountdownConfig } from "ngx-countdown";
import { JwtHelperService } from "@auth0/angular-jwt";
import { ValidatorService } from '../shared/services/validator.service';
import { AppToastService } from '../core/services/app-toast.service';

/**
 * Role titles (as issued in the token's `role` claim) that must not reach the
 * Admin CRM. These are the roles VenueStaffService assigns to venue staff, who
 * belong in the Partner Portal - it signs in through the same User/VerifyOTP
 * endpoint, so the token is issued either way and the CRM has to turn them
 * away itself.
 * To restore a role's CRM access, remove it from this list.
 */
export const CRM_BLOCKED_ROLES = [
    'partner_venue_personaltrainer',
    'partner_venue_generaltrainer',
    'partner_venue_manager',
    'partner_venue_mc',
    // Never assigned by any code path today, and rejected by VerifyOtpRole
    // before a token is issued. Listed so it stays blocked if that changes.
    'partner_venue_staff',
    // Affiliate gyms use the Affiliate Portal (FitZone.Affiliate.WebPortal).
    'affiliate_gym_owner',
    'affiliate_gym_staff',
    'affiliate_gym_zonal_manager',
    'affiliate_gym_member_coordinator',
    // An affiliate gym's attendance account signs in to the Attendance App only.
    'affiliate_gym_attendance_recorder',
];

@Component({
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})

export class LoginComponent implements OnInit, OnDestroy {

    @ViewChild('cd', { static: false }) public countdown!: CountdownComponent;
    countdownConfig: CountdownConfig = {
        leftTime: 0, // 24 hours in milliseconds
        demand: true,
        notify: 0,
    };

    private countdownInterval: any;
    private tokenExpirationTime: number = 0;

    rememberMe: boolean = false;
    isOtpSent: boolean = false;
    isResentOTP = false;
    resendOtpCount: number = 0;
    wrongOtpCount: number = 0;
    email: FormControl = new FormControl();
    // otp: FormControl = new FormControl();
    otp1: FormControl = new FormControl();
    otp2: FormControl = new FormControl();
    otp3: FormControl = new FormControl();
    otp4: FormControl = new FormControl();
    error: any;
    emailForm: any;
    countryCode: string = '+91';


    constructor(
        private layoutService: LayoutService,
        private toasterService: AppToastService,
        private loginService: LoginService, 
        private fb: FormBuilder,
        private storageService: StorageHelperService, 
        private validatorService: ValidatorService,
        private router: Router,

    ) {
        this.otp1.setValidators([Validators.required]);
        this.otp2.setValidators([Validators.required]);
        this.otp3.setValidators([Validators.required]);
        this.otp4.setValidators([Validators.required]);
        if (this.storageService.getItemFromSession("isLoggedIn")) {
            this.router.navigate(['/home']);
        }
    }

    ngOnInit(): void {
        this.emailForm = this.fb.group({
            email: ['', [Validators.required, (control: AbstractControl) => this.emailOrPhoneValidator(control)]]
        });
        this.initializeTimer();
    }

    emailOrPhoneValidator(control: AbstractControl): { [key: string]: any } | null {
        const value = control.value?.trim();
        if (!value) return null;

        const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,4})+$/;
        const phoneRegex = /^\d{10}$/;

        const isValidEmail = emailRegex.test(value);
        const isValidPhone = phoneRegex.test(value);

        if (!isValidEmail && !isValidPhone) {
            return { invalidEmailOrPhone: true };
        }
        return null;
    }

    ngOnDestroy(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
    }

    initializeTimer(): void {
        const accessToken = this.storageService.getItemFromSession('accessToken');
        if (accessToken) {
            this.tokenExpirationTime = this.getTokenExpirationTime();
            this.countdownConfig.leftTime = this.tokenExpirationTime - Date.now();
            this.startCountdown();
        }
    }

    startCountdown(): void {
        this.countdownInterval = setInterval(() => {
            const remainingTime = this.tokenExpirationTime - Date.now();
            if (remainingTime <= 0) {
                this.refreshToken();
            }
        }, 1000);
    }

    refreshToken(): void {
        const accessToken = this.storageService.getItemFromSession('accessToken');
        const refreshToken = this.storageService.getItemFromSession('refreshToken');

        if (refreshToken) {
            this.loginService.refreshToken(accessToken, refreshToken).subscribe({
                next: (response: any) => {
                    this.storageService.setItemToSession('accessToken', response.token.accessToken);
                    this.storageService.setItemToSession('refreshToken', response.token.refreshToken);
                    this.tokenExpirationTime = this.getTokenExpirationTime();
                    this.countdownConfig.leftTime = this.tokenExpirationTime - Date.now();
                    clearInterval(this.countdownInterval);
                    this.startCountdown();
                },
                error: (error) => {
                    console.error('Error refreshing token:', error);
                    this.router.navigate(['/login']); // Redirect to login on error
                }
            });
        }
    }

    getTokenExpirationTime(): number {
        const helper = new JwtHelperService();
        const token = this.storageService.getItemFromSession('accessToken');
        const expirationDate = helper.getTokenExpirationDate(token);
        return expirationDate ? expirationDate.getTime() : Date.now();
    }

    handleTokenExpiration(): void {
        // Check if the access token is available
        const accessToken = sessionStorage.getItem('accessToken');
        const refreshToken = sessionStorage.getItem('refreshToken');

        // If no access token or refresh token, it might mean the user is not authenticated
        if (!accessToken || !refreshToken) {
            console.log('No access token or refresh token found. User may not be authenticated.');
            return;
        }

        // Check if the access token is expired or about to expire
        const tokenExpirationTime = this.getTokenExpirationTime();

        if (Date.now() > tokenExpirationTime) {
            console.log('Access token expired. Refreshing...');
            this.refreshToken();  // Pass the tokens as arguments
        } else {
            console.log('Access token is still valid.');
        }
    }


    sendOtp() {
        this.otp1.reset();
        this.otp2.reset();
        this.otp4.reset();
        this.otp3.reset();
        const emailControl = this.emailForm.controls['email'];
        emailControl.markAsTouched();
        if (this.emailForm.valid) {
            this.error = '';
            const input = this.emailForm.value.email.trim();
            const isMobileNumber = /^\d{10}$/.test(input);

            let payload: any;
            if (isMobileNumber) {
                payload = {
                    mobileNumber: {
                        countryCode: this.countryCode,
                        number: input
                    },
                    otpGenAttempt: 1
                };
            } else {
                payload = {
                    email: input,
                    otpGenAttempt: 1
                };
            }

            this.loginService.generateToken(payload).subscribe({
                next: (otpDetails: any) => {
                    this.isOtpSent = true;
                    if (this.isOtpSent) {
                        this.toasterService.showSuccess('OTP sent successfully');
                        this.isResentOTP = false;
                    }
                    this.storageService.setItemToSession('userID', otpDetails.userID);
                    document.getElementById('otp1')?.focus();
                },
                error: (err) => {
                    this.error = err.error.errors.Email?.[0] || err.error.errors.mobileNumber?.[0] || 'An error occurred';
                },
            });
        }
    }


    handleTimerEvent(event: any) {
        if (event.left === 0 && this.resendOtpCount < 2) {
            this.isResentOTP = true;
        }
    }

    keyUp1(e: any) {
        if (e.target.value.length == 1 && e.key !== 'Backspace') {
            document.getElementById('otp2')?.focus();
        } else if (e.key === 'Backspace') {
            document.getElementById('otp1')?.focus();
        }
    }

    keyUp2(e: any) {
        if (e.target.value.length == 1 && e.key !== 'Backspace') {
            document.getElementById('otp3')?.focus();
        } else if (e.key === 'Backspace') {
            document.getElementById('otp1')?.focus();
        }
    }

    keyUp3(e: any) {
        if (e.target.value.length == 1 && e.key !== 'Backspace') {
            document.getElementById('otp4')?.focus();
        } else if (e.key === 'Backspace') {
            document.getElementById('otp2')?.focus();
        }
    }

    keyUp4(e: any) {
        if (e.key === 'Backspace') {
            document.getElementById('otp3')?.focus();
        }
    }

    /**
     * @description submit OTP to validate and login
     */
    submitOtp() {
        if (this.otp1.valid && this.otp2.valid && this.otp3.valid && this.otp4.valid) {
            const params = {
                userid: this.storageService.getItemFromSession('userID'),
                otp: this.otp1.value.toString() + this.otp2.value.toString() + this.otp3.value.toString() + this.otp4.value.toString(),
            };
            this.loginService.validateOtp(params).subscribe({
                next: (loginDetails: any) => {
                    if (loginDetails) {
                        const helper = new JwtHelperService();

                        const decodedToken = helper.decodeToken(
                            loginDetails.accessToken
                        );

                        // Checked before anything is persisted, so a blocked role
                        // never leaves a usable token behind in the session.
                        if (CRM_BLOCKED_ROLES.includes(decodedToken?.role)) {
                            this.denyCrmAccess();
                            return;
                        }

                        this.storageService.setItemToSession('accessToken', loginDetails.accessToken);
                        this.storageService.setItemToSession('refreshToken', loginDetails.refreshToken);
                        this.storageService.setItemToSession('refreshTokenExpiry', loginDetails.refreshTokenExpiry);
                        this.storageService.setItemToSession("isLoggedIn", loginDetails);

                        // Other functions
                        const expirationDate = helper.getTokenExpirationDate(
                            loginDetails.accessToken
                        );
                        const isExpired = helper.isTokenExpired(
                            loginDetails.accessToken
                        );
                        console.log(decodedToken, expirationDate, isExpired);
                        sessionStorage.setItem('Access', JSON.stringify(decodedToken.applicationclaims));
                        this.callApis();
                        this.router.navigate(['/home']);
                    }
                },
                error: (err) => {
                    if (!err.error.issuccess && err.error.statusmessage == 'Wrong OTP has been entered!') {
                        this.wrongOtpCount++;
                        if (this.wrongOtpCount == 2) {
                            this.isOtpSent = false;
                            this.toasterService.showError('Please login again.');
                            this.wrongOtpCount = 0;

                        }
                    }
                    this.otp1.reset();
                    this.otp2.reset();
                    this.otp4.reset();
                    this.otp3.reset();
                },
            });
        }
    }

    /**
     * Sends a blocked role back to the email step. Wipes the session because
     * sendOtp() has already stored userID by this point, and drops the OTP so
     * the stale code cannot be resubmitted.
     */
    private denyCrmAccess() {
        this.storageService.clearSessionStorage();
        this.otp1.reset();
        this.otp2.reset();
        this.otp3.reset();
        this.otp4.reset();
        this.isOtpSent = false;
        this.isResentOTP = false;
        this.resendOtpCount = 0;
        this.wrongOtpCount = 0;
        this.error = 'This account does not have access to the Admin CRM.';
        this.toasterService.showError('This account does not have access to the Admin CRM.');
    }

    callApis() {
        const getAllRoles$ = this.loginService.getAllRoles();
        const getAllVenues$ = this.loginService.fetchAllVenuesByUser(
            this.storageService.getItemFromSession("userID")
        );
        forkJoin([getAllRoles$]).subscribe({
            next: (res: any) => {
                this.storageService.setItemToSession('allRoles', res[0]);
            },
        });
    }
    otpCount() {
        if (this.resendOtpCount < 2) {
            this.resendOtpCount++;
        }
    }
}
